import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  SyncOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import {
  isActiveISOSettingSelector, // [crm-iso]
  userSelector,
} from "appredux/selectors";
import { SCHEDULE_STATUS } from "components/apps/MeetingSchedule/scheduleStatus";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useAntdMessage } from "hooks/useAntdMessage";

// Nama → user_id
const REPORT_NOTIFY_USERS = {
  OGI: 2,
  SALSA: 22974,
  ADI: 87677,
  YASO: 7092,
  RULLY: 532359,
  MEY: 277064,
  ASA: 490132,
  ULIN: 51541,
};
const U = REPORT_NOTIFY_USERS;

// admin_group_id → penerima notif report_updated (selain admin meeting).
// Ogi (U.OGI) ikut di semua grup.
const REPORT_NOTIFY_BY_GROUP = {
  4: [U.SALSA, U.ADI, U.YASO, U.OGI], // Kledo SC
  7: [U.RULLY, U.YASO, U.OGI], // Kledo IC
  8: [U.SALSA, U.ADI, U.YASO, U.OGI], // Kledo POS
  9: [U.MEY, U.YASO, U.OGI], // GajiHub IC
  10: [U.ASA, U.ULIN, U.YASO, U.OGI], // GajiHub SC
};

const TITLE_KEY = {
  meeting_schedule_created: "meeting_schedule.notif_created",
  meeting_schedule_rescheduled: "meeting_schedule.notif_rescheduled",
  meeting_schedule_contact_changed: "meeting_schedule.notif_contact_changed",
  meeting_schedule_status_changed: "meeting_schedule.notif_status_changed",
  meeting_schedule_report_updated: "meeting_schedule.notif_report_updated",
  meeting_schedule_deleted: "meeting_schedule.notif_deleted",
};

// Judul khusus untuk status_changed — mengikuti status barunya.
const STATUS_TITLE_KEY = {
  [SCHEDULE_STATUS.NEW]: "meeting_schedule.notif_status_new",
  [SCHEDULE_STATUS.CONFIRMED]: "meeting_schedule.notif_status_confirmed",
  [SCHEDULE_STATUS.DONE]: "meeting_schedule.notif_status_done",
  [SCHEDULE_STATUS.CANCELLED]: "meeting_schedule.notif_status_cancelled",
  [SCHEDULE_STATUS.NO_SHOW]: "meeting_schedule.notif_status_no_show",
  [SCHEDULE_STATUS.PENAWARAN_ZOOM]: "meeting_schedule.notif_status_zoom",
  [SCHEDULE_STATUS.KONFIRMASI_HADIR]: "meeting_schedule.notif_status_attend",
};

const getNotifTitleKey = (payload) => {
  if (payload.data_type === "meeting_schedule_status_changed") {
    const st = Number(payload.new_status_id ?? payload.status_id);
    return STATUS_TITLE_KEY[st] || TITLE_KEY.meeting_schedule_status_changed;
  }
  return TITLE_KEY[payload.data_type];
};

const DEDUPE_WINDOW_MS = 1000;

// Tema (ikon + warna aksen) per tipe notifikasi. Latar tetap putih (default).
const getNotifTheme = (payload) => {
  switch (payload.data_type) {
    case "meeting_schedule_deleted":
      return { icon: <DeleteOutlined />, color: "#ef4444" };
    case "meeting_schedule_report_updated":
      return { icon: <FileTextOutlined />, color: "#10b981" };
    case "meeting_schedule_created":
      return { icon: <CalendarOutlined />, color: "#3b82f6" };
    case "meeting_schedule_rescheduled":
      return { icon: <ClockCircleOutlined />, color: "#f59e0b" };
    case "meeting_schedule_contact_changed":
      return { icon: <UserSwitchOutlined />, color: "#3b82f6" };
    case "meeting_schedule_status_changed": {
      const st = Number(payload.new_status_id ?? payload.status_id);
      if (st === SCHEDULE_STATUS.CANCELLED)
        return { icon: <CloseCircleOutlined />, color: "#ef4444" };
      if (st === SCHEDULE_STATUS.NO_SHOW)
        return { icon: <ExclamationCircleOutlined />, color: "#f97316" };
      if (
        st === SCHEDULE_STATUS.DONE ||
        st === SCHEDULE_STATUS.CONFIRMED ||
        st === SCHEDULE_STATUS.KONFIRMASI_HADIR
      )
        return { icon: <CheckCircleOutlined />, color: "#10b981" };
      return { icon: <SyncOutlined />, color: "#3b82f6" };
    }
    default:
      return { icon: <CalendarOutlined />, color: "#3b82f6" };
  }
};

/**
 * Menampilkan toast notifikasi (read-only) untuk event meeting schedule dari
 * pusher WASyncChanged. Hanya untuk admin meeting yang sedang login; khusus
 * report_updated juga utk penerima per grup (REPORT_NOTIFY_BY_GROUP).
 */
const useMeetingScheduleNotificationHandler = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notification } = useAntdMessage();
  const currentUser = useSelector(userSelector);
  const isActiveISO = useSelector(isActiveISOSettingSelector); // [crm-iso]
  const recentRef = useRef(new Map());

  const meetingScheduleNotificationHandler = useCallback(
    (payload) => {
      // [crm-iso] Agen terpengaruh tidak menerima notifikasi meeting schedule.
      // TEARDOWN: hapus baris di bawah + isActiveISO dari deps.
      if (isActiveISO) return;
      const type = payload?.data_type;
      if (!type || !TITLE_KEY[type]) return;

      const userId = Number(currentUser?.id);

      // Filter penerima: admin meeting = user login (payload.user_id dari BE).
      let allowed = Number(payload.user_id) === userId;
      // report_updated: tambahan penerima per grup (berdasar user id).
      if (!allowed && type === "meeting_schedule_report_updated") {
        const extra = REPORT_NOTIFY_BY_GROUP[Number(payload.admin_group_id)];
        allowed = Array.isArray(extra) && extra.includes(userId);
      }
      if (!allowed) return;

      // Dedupe anti-spam toast (window pendek).
      const dedupeKey = `${payload.meeting_schedule_id}:${type}`;
      const now = Date.now();
      const last = recentRef.current.get(dedupeKey);
      if (last && now - last < DEDUPE_WINDOW_MS) return;
      recentRef.current.set(dedupeKey, now);

      const lines = String(payload.message || "").split("\n");
      const theme = getNotifTheme(payload);
      // Tombol "Lihat" full width — ditaruh di description agar memenuhi lebar.
      const viewBtn =
        type !== "meeting_schedule_deleted" ? (
          <Button
            block
            type="primary"
            icon={<EyeOutlined />}
            className="mt-3"
            style={{ backgroundColor: theme.color, borderColor: theme.color }}
            onClick={() => {
              navigate(
                `/meeting-management?schedule_id=${payload.meeting_schedule_id}`
              );
              notification.destroy(dedupeKey);
            }}
          >
            {t("meeting_schedule.notif_view")}
          </Button>
        ) : null;

      notification.open({
        key: dedupeKey,
        message: t(getNotifTitleKey(payload)),
        description: (
          <div>
            {lines.map((line, i) => (
              <div key={i}>{line || " "}</div>
            ))}
            {viewBtn}
          </div>
        ),
        icon: (
          <span style={{ color: theme.color, fontSize: 20 }}>{theme.icon}</span>
        ),
        style: { borderInlineStart: `4px solid ${theme.color}` },
        duration: 0, // tidak auto-hilang; tutup manual (atau via "Lihat")
      });
    },
    [t, navigate, notification, currentUser, isActiveISO] // [crm-iso] deps: isActiveISO
  );

  return { meetingScheduleNotificationHandler };
};

export default useMeetingScheduleNotificationHandler;
